import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
  collection, getDocs, addDoc, updateDoc,
  doc, serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { writeAuditLog } from '../utils/auditLog';

const ROLES = [
  { value: 'admin',      label: 'Admin',      desc: 'Full access + invite users' },
  { value: 'pharmacist', label: 'Pharmacist',  desc: 'Full access, no invites'    },
  { value: 'technician', label: 'Technician',  desc: 'Dashboard + Alerts only'   },
];

export default function TeamPage() {
  const { pharmacy, role, user }     = useAuth();
  const [users, setUsers]      = useState([]);
  const [invites, setInvites]  = useState([]);
  const [email, setEmail]      = useState('');
  const [inviteRole, setInviteRole] = useState('pharmacist');
  const [loading, setLoading]  = useState(true);
  const [sending, setSending]  = useState(false);
  const [msg, setMsg]          = useState('');

  // Role guard — admin or superadmin only
  const isAllowed = role === 'admin' || role === 'superadmin';

  const loadData = async () => {
    if (!pharmacy) return;
    const [uSnap, iSnap] = await Promise.all([
      getDocs(collection(db, 'pharmacies', pharmacy.id, 'users')),
      getDocs(collection(db, 'pharmacies', pharmacy.id, 'invites')),
    ]);
    setUsers(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setInvites(iSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { if (isAllowed) loadData(); }, [pharmacy]);

  if (!isAllowed) {
    return (
      <div style={{ padding: '28px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A' }}>Team</h1>
        <p style={{ color: '#94A3B8' }}>Admin access required.</p>
      </div>
    );
  }

  const sendInvite = async () => {
    if (!email.trim()) return;
    setSending(true);
    setMsg('');
    try {
      await addDoc(collection(db, 'pharmacies', pharmacy.id, 'invites'), {
        email:     email.toLowerCase().trim(),
        role:      inviteRole,
        status:    'pending',
        invitedBy: auth.currentUser.email,
        createdAt: serverTimestamp(),
      });
      await writeAuditLog(pharmacy.id, user || auth.currentUser, {
        action: 'Invite sent',
        entityType: 'invite',
        entityName: email.toLowerCase().trim(),
        newValue: inviteRole,
        actorRole: role || '',
        source: 'client',
      });
      setMsg(`Invite sent to ${email.trim()}`);
      setEmail('');
      await loadData();
    } catch (err) {
      setMsg('Error: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const changeRole = async (userId, newRole) => {
    const target = users.find(u => u.id === userId);
    const oldRole = target?.role || '';
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'users', userId), { role: newRole });
    await writeAuditLog(pharmacy.id, user || auth.currentUser, {
      action: 'User role changed',
      entityType: 'user',
      entityId: userId,
      entityName: target?.email || userId,
      previousValue: oldRole,
      newValue: newRole,
      actorRole: role || '',
      source: 'client',
    });
    setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
  };

  const deactivate = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;
    const target = users.find(u => u.id === userId);
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'users', userId), { active: false });
    await writeAuditLog(pharmacy.id, user || auth.currentUser, {
      action: 'User deactivated',
      entityType: 'user',
      entityId: userId,
      entityName: target?.email || userId,
      previousValue: 'active',
      newValue: 'inactive',
      actorRole: role || '',
      source: 'client',
    });
    setUsers(users.map(u => u.id === userId ? { ...u, active: false } : u));
  };

  const reactivate = async (userId) => {
    const target = users.find(u => u.id === userId);
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'users', userId), { active: true });
    await writeAuditLog(pharmacy.id, user || auth.currentUser, {
      action: 'User reactivated',
      entityType: 'user',
      entityId: userId,
      entityName: target?.email || userId,
      previousValue: 'inactive',
      newValue: 'active',
      actorRole: role || '',
      source: 'client',
    });
    setUsers(users.map(u => u.id === userId ? { ...u, active: true } : u));
  };

  const pendingInvites = invites.filter(i => i.status === 'pending');

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Header */}
      <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 800, color: '#0F172A' }}>Team</h1>
      <p style={{ margin: '0 0 28px', fontSize: 13, color: '#94A3B8' }}>{pharmacy?.name}</p>

      {/* Invite card */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0',
        padding: 24, marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0F172A' }}>
          Invite New User
        </h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500,
              color: '#374151', marginBottom: 5 }}>Email Address</label>
            <input type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendInvite()}
              placeholder="user@pharmacy.ca"
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB',
                borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500,
              color: '#374151', marginBottom: 5 }}>Role</label>
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              style={{ padding: '9px 12px', border: '1px solid #D1D5DB',
                borderRadius: 8, fontSize: 14 }}>
              {ROLES.map(r => (
                <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>
              ))}
            </select>
          </div>
          <button onClick={sendInvite} disabled={sending || !email.trim()} style={{
            padding: '9px 22px', background: sending ? '#94A3B8' : '#0F172A',
            color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600,
            fontSize: 14, cursor: sending ? 'not-allowed' : 'pointer',
          }}>
            {sending ? 'Sending…' : 'Send Invite'}
          </button>
        </div>
        {msg && (
          <p style={{
            marginTop: 12, fontSize: 13, fontWeight: 500,
            color: msg.startsWith('Error') ? '#DC2626' : '#16A34A',
          }}>{msg}</p>
        )}
      </div>

      {/* Current users */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Loading…</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0',
          overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #F1F5F9' }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0F172A' }}>
              Current Users
            </h3>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['Name', 'Email', 'Role', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: 'left', fontSize: 11,
                    fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase',
                    letterSpacing: '0.05em', borderBottom: '1px solid #F1F5F9',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const isInactive = u.active === false;
                const isSelf     = u.id === auth.currentUser?.uid;
                return (
                  <tr key={u.id} style={{
                    borderBottom: '1px solid #F8FAFC',
                    opacity: isInactive ? 0.5 : 1,
                  }}>
                    <td style={{ padding: '11px 16px', fontWeight: 600, color: '#0F172A' }}>
                      {u.displayName || '—'}
                      {isSelf && <span style={{ fontSize: 10, color: '#94A3B8', marginLeft: 6 }}>(you)</span>}
                    </td>
                    <td style={{ padding: '11px 16px', color: '#64748B' }}>{u.email}</td>
                    <td style={{ padding: '11px 16px' }}>
                      <select value={u.role}
                        onChange={e => changeRole(u.id, e.target.value)}
                        disabled={isInactive || isSelf}
                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #E2E8F0',
                          fontSize: 13, color: '#0F172A' }}>
                        {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: isInactive ? '#F1F5F9' : '#DCFCE7',
                        color:      isInactive ? '#94A3B8' : '#16A34A',
                      }}>
                        {isInactive ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      {!isSelf && (
                        isInactive ? (
                          <button onClick={() => reactivate(u.id)} style={{
                            padding: '4px 12px', background: '#DCFCE7', color: '#16A34A',
                            border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                          }}>Reactivate</button>
                        ) : (
                          <button onClick={() => deactivate(u.id)} style={{
                            padding: '4px 12px', background: '#FEE2E2', color: '#DC2626',
                            border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                          }}>Deactivate</button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 32, textAlign: 'center', color: '#94A3B8' }}>
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending invites */}
      {pendingInvites.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0',
          overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #F1F5F9',
            display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0F172A' }}>
              Pending Invites
            </h3>
            <span style={{
              padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700,
              background: '#FEF3C7', color: '#D97706',
            }}>{pendingInvites.length}</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['Email', 'Role', 'Invited By', 'Date Sent'].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: 'left', fontSize: 11,
                    fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase',
                    letterSpacing: '0.05em', borderBottom: '1px solid #F1F5F9',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pendingInvites.map(i => (
                <tr key={i.id} style={{ borderBottom: '1px solid #F8FAFC' }}>
                  <td style={{ padding: '11px 16px', fontWeight: 500, color: '#0F172A' }}>{i.email}</td>
                  <td style={{ padding: '11px 16px', color: '#64748B', textTransform: 'capitalize' }}>{i.role}</td>
                  <td style={{ padding: '11px 16px', color: '#64748B' }}>{i.invitedBy}</td>
                  <td style={{ padding: '11px 16px', color: '#64748B' }}>
                    {i.createdAt?.toDate?.().toLocaleDateString() ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
